import { useEffect, useRef } from 'react';

import { ApiError } from '../../lib/api/errors.js';

/** The sign-in error messages (`signIn.errors.*`). */
export type SignInErrorCode =
  | 'rate_limited'
  | 'method_unavailable'
  | 'account_unavailable'
  | 'restart_sign_in'
  | 'network_error'
  | 'generic';

/**
 * The message key for a failed sign-in call: one of `expected` when the API
 * said so, `network_error` when it could not be reached, else `generic`.
 */
export function errorCode(error: unknown, expected: readonly SignInErrorCode[]): SignInErrorCode {
  if (!(error instanceof ApiError)) return 'generic';
  if (error.status === 0) return 'network_error';
  return (expected as readonly string[]).includes(error.code)
    ? (error.code as SignInErrorCode)
    : 'generic';
}

/**
 * The moment of success: a sage tick that pops in and draws itself, with a
 * title and a line of text, announced to screen readers. With reduced
 * motion it simply appears.
 */
export function SignedIn({ title, body }: { title: string; body: string }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <div className="signed-in" role="status">
      <svg className="signed-in__tick motion-pop" viewBox="0 0 48 48" aria-hidden="true">
        <circle cx="24" cy="24" r="24" />
        <path className="motion-tick-draw" d="M14 25l7 7 13-15" pathLength={100} />
      </svg>
      <h1 className="sign-in__step-title sign-in__step-title--done" tabIndex={-1} ref={heading}>
        {title}
      </h1>
      <p>{body}</p>
    </div>
  );
}
